/**
 * Line chart for the admin dashboard's time series (e.g. new users in the last 30 days).
 *
 * Presentational: the Dashboard page loads the metrics (with its own loading/error states)
 * and passes each series in. Smoothed line with visible axes, per the Figma design.
 */

import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export interface ChartDataPoint {
  /** Month label (e.g., "Jan", "Feb") */
  month: string;
  /** Numeric value for this month */
  value: number;
}

interface LineChartProps {
  /** Title of the chart */
  title: string;
  /** Array of data points to display */
  data: ChartDataPoint[];
  /** Optional CSS class for custom styling */
  className?: string;
}

/**
 * Line chart component with smoothed curves.
 *
 * Displays data as a line chart with visible Y and X axes.
 * Uses monotone curve type for smooth, natural-looking lines.
 *
 * @param props - Component props
 * @returns Line chart UI element
 */
export const DashboardLineChart: React.FC<LineChartProps> = ({ title, data, className = '' }) => {
  // Calculate max value for Y-axis scaling (round up to nearest 10)
  const maxValue = Math.max(...data.map((d) => d.value), 1);
  const yAxisMax = Math.ceil(maxValue / 10) * 10 || 10;

  // Detect dark mode
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const html = document.documentElement;
      const theme = html.getAttribute('data-theme');
      setIsDark(
        theme === 'dark' || (!theme && window.matchMedia('(prefers-color-scheme: dark)').matches)
      );
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkTheme);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', checkTheme);
    };
  }, []);

  // Colors that adapt to theme
  const axisColor = '#FFFFFF';
  const tickColor = '#FFFFFF';
  const tickLineColor = '#FFFFFF';

  return (
    <div className={`card bg-[#1E1E1E] shadow-md rounded-2xl ${className}`}>
      <div className="card-body">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <XAxis
                dataKey="month"
                stroke={axisColor}
                tick={{
                  fill: tickColor,
                  fontSize: 12,
                }}
                tickLine={{ stroke: tickLineColor }}
              />
              <YAxis
                stroke={axisColor}
                tick={{
                  fill: tickColor,
                  fontSize: 12,
                }}
                tickLine={{ stroke: tickLineColor }}
                domain={[0, yAxisMax]}
                width={40}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: isDark
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: isDark ? '#f3f4f6' : '#1f2937',
                }}
                labelStyle={{
                  color: isDark ? '#f3f4f6' : '#1f2937',
                  fontWeight: 600,
                }}
                itemStyle={{
                  color: isDark ? '#f3f4f6' : '#1f2937',
                }}
              />
              <Line type="monotone" dataKey="value" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
